/* global SideDrawer */

const CART_DRAWER_STORAGE_DISCOUNT = 'cart-drawer-discount-open';
const CART_DRAWER_STORAGE_CODES = 'cart-drawer-discount-codes';

/**
 * Bind once per page load — survives cart-drawer disconnect/reconnect
 * after section merges so the nav cart icon never falls through to /cart.
 */
function ensureGlobalCartDrawerOpeners() {
  if (window.__cuevasCartDrawerOpenersBound) return;
  window.__cuevasCartDrawerOpenersBound = true;

  const getDrawer = () => document.querySelector('cart-drawer.js-cart-drawer, cart-drawer, .js-cart-drawer');

  document.addEventListener(
    'click',
    (evt) => {
      if (!window.theme || !theme.settings || theme.settings.cartType !== 'drawer') return;
      if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button === 1) return;
      const link = evt.target.closest && evt.target.closest('a.cart-link');
      if (!link) return;
      const drawer = getDrawer();
      if (!drawer || typeof drawer.open !== 'function') return;
      evt.preventDefault();
      evt.stopPropagation();
      drawer.open(link);
    },
    true
  );

  document.addEventListener('theme:open-cart-drawer', (evt) => {
    const drawer = getDrawer();
    if (!drawer || typeof drawer.open !== 'function') return;
    drawer.open(evt.detail && evt.detail.opener ? evt.detail.opener : null);
  });

  document.addEventListener('dispatch:cart-drawer:open', (evt) => {
    const drawer = getDrawer();
    if (!drawer || typeof drawer.open !== 'function') return;
    drawer.open(evt.detail && evt.detail.opener ? evt.detail.opener : null);
  });
}

ensureGlobalCartDrawerOpeners();

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
    this.initDiscountForm();
    this.initSizeEditor();
    ensureGlobalCartDrawerOpeners();
  }

  bindEvents() {
    this.cartRefreshHandler = this.cartRefreshHandler.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      if (typeof this.syncDiscountFromCart === 'function') {
        this.syncDiscountFromCart();
      }
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.initDiscountForm();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  cartRefreshHandler() {
    const cartForm = this.querySelector('cart-form');
    if (cartForm && typeof cartForm.refresh === 'function') cartForm.refresh();
  }

  updateFromCartChange(html) {
    const cartForm = this.querySelector('cart-form');
    if (cartForm && typeof cartForm.refreshFromHtml === 'function') {
      cartForm.refreshFromHtml(html);
    }
  }

  initDiscountForm() {
    this.initDiscountAccordion();

    const wrap = this.querySelector('.js-discount-wrapper');
    const input = this.querySelector('.js-discount-input');
    const btn = this.querySelector('.js-apply-discount');
    const label = this.querySelector('.js-apply-discount-label');
    const feedback = this.querySelector('.js-discount-feedback');

    if (!wrap || !input || !btn || !feedback) return;
    if (input.dataset.bound === 'true') return;
    input.dataset.bound = 'true';

    const messages = {
      apply: wrap.dataset.labelApply || 'Apply',
      applying: wrap.dataset.labelApplying || 'Applying',
      notFound: wrap.dataset.errorNotFound || 'Discount code not found',
      already: wrap.dataset.errorAlready || 'This code is already applied',
      notApplicable: wrap.dataset.errorNotApplicable || 'This code is not valid for this order',
      generic: wrap.dataset.errorGeneric || 'This code couldn’t be applied'
    };

    const badgeIcon = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';

    const escapeHtml = (value) => String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const codeMatches = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

    const renderAppliedDiscountBadges = (cart) => {
      const container = wrap.querySelector('[data-merge="applied-discounts"]');
      if (!container || !cart || !cart.items || !cart.items.length) {
        if (container) container.innerHTML = '';
        return;
      }

      const titles = [];
      const pushTitle = (title) => {
        const cleaned = (title || '').trim();
        if (!cleaned) return;
        if (titles.some((t) => codeMatches(t, cleaned))) return;
        titles.push(cleaned);
      };

      (cart.cart_level_discount_applications || []).forEach((entry) => pushTitle(entry.title));
      (cart.discount_applications || []).forEach((entry) => {
        if (entry.target_type === 'shipping_line') pushTitle(entry.title);
      });
      (cart.items || []).forEach((item) => {
        (item.line_level_discount_allocations || []).forEach((allocation) => {
          const title = allocation.discount_application && allocation.discount_application.title;
          pushTitle(title);
        });
      });
      (cart.discount_codes || []).forEach((entry) => {
        if (entry.code && entry.applicable !== false) pushTitle(entry.code);
      });

      if (!titles.length) {
        container.innerHTML = '';
        return;
      }

      const badges = titles.map((title) => (
        `<span class="cart-drawer__discount-badge">${badgeIcon}${escapeHtml(title)}</span>`
      )).join('');

      container.innerHTML = `<div class="cart-drawer__applied-discounts">${badges}</div>`;
    };

    const ensureShippingLineVisible = () => {
      const summary = this.querySelector('.cart-drawer__summary');
      if (!summary || summary.querySelector('.cart-drawer__shipping')) return;

      const shippingAmount = wrap.dataset.shippingAmount || '$6.00';
      const shippingLabel = wrap.dataset.shippingLabel || 'Shipping';
      const freeLabel = wrap.dataset.shippingFreeLabel || 'Free';
      const row = document.createElement('div');
      row.className = 'cart-drawer__shipping opposing-items';
      row.innerHTML = `
        <span class="cart-drawer__shipping-label">${escapeHtml(shippingLabel)}</span>
        <span class="cart-drawer__shipping-amount">
          <s class="cart-drawer__shipping-was theme-money">${escapeHtml(shippingAmount)}</s>
          <span>${escapeHtml(freeLabel)}</span>
        </span>
      `;

      const subtotal = summary.querySelector('.cart-drawer__subtotal');
      if (subtotal) {
        summary.insertBefore(row, subtotal);
      } else {
        summary.appendChild(row);
      }
    };

    const refreshCartUi = async () => {
      const cartForm = this.querySelector('cart-form');
      if (cartForm && typeof cartForm.refresh === 'function') {
        await cartForm.refresh();
      }
    };

    const fetchCart = async () => {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      return res.json();
    };

    const findCode = (cart, code) => {
      const list = cart.discount_codes || [];
      return list.find((entry) => codeMatches(entry.code, code));
    };

    const getRememberedCodes = () => {
      try {
        const raw = sessionStorage.getItem(CART_DRAWER_STORAGE_CODES);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        return [];
      }
    };

    const rememberCodes = (...values) => {
      const merged = getRememberedCodes();
      values.forEach((value) => {
        const cleaned = (value || '').trim();
        if (!cleaned) return;
        if (!merged.some((entry) => codeMatches(entry, cleaned))) merged.push(cleaned);
      });
      try {
        sessionStorage.setItem(CART_DRAWER_STORAGE_CODES, JSON.stringify(merged));
      } catch (err) {
        /* ignore */
      }
    };

    const syncRememberedFromCart = (cart) => {
      if (!cart) return;
      (cart.discount_codes || []).forEach((entry) => {
        if (entry && entry.code) rememberCodes(entry.code);
      });
    };

    const readCookieDiscount = () => {
      const match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/i);
      return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : '';
    };

    const captureDiscountFromUrl = () => {
      const match = window.location.pathname.match(/\/discount\/([^/?#]+)/i);
      if (match && match[1]) rememberCodes(decodeURIComponent(match[1]));
    };

    captureDiscountFromUrl();
    const cookieCode = readCookieDiscount();
    if (cookieCode) rememberCodes(cookieCode);

    // Merge session, cookie, cart, and visible chips so email-link shipping codes survive
    const getStackedCodes = (cart, nextCode) => {
      const stacked = [];
      const add = (value) => {
        const cleaned = (value || '').trim();
        if (!cleaned) return;
        if (!stacked.some((entry) => codeMatches(entry, cleaned))) stacked.push(cleaned);
      };

      getRememberedCodes().forEach(add);
      const freshCookie = readCookieDiscount();
      if (freshCookie) add(freshCookie);
      (cart.discount_codes || []).forEach((entry) => {
        if (entry && entry.code) add(entry.code);
      });
      wrap.querySelectorAll('.cart-drawer__discount-badge').forEach((badge) => {
        add(badge.textContent.replace(/\s+/g, ' ').trim());
      });
      add(nextCode);
      return stacked;
    };

    const updateCartDiscounts = async (discountString) => {
      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: discountString })
      });
      try {
        return await updateRes.json();
      } catch (err) {
        return null;
      }
    };

    const countPresentCodes = (cart, codes) => {
      if (!cart || cart.status) return 0;
      return codes.filter((entry) => findCode(cart, entry)).length;
    };

    const applyStackedDiscount = async (codes) => {
      if (!codes.length) return null;
      if (codes.length === 1) return updateCartDiscounts(codes[0]);

      const attempts = [
        codes.join(','),
        [...codes].reverse().join(',')
      ];
      const uniqueAttempts = [...new Set(attempts)];

      let bestCart = null;
      let bestCount = -1;

      for (const discount of uniqueAttempts) {
        const cart = await updateCartDiscounts(discount);
        if (!cart || cart.status) continue;
        const present = countPresentCodes(cart, codes);
        if (present > bestCount) {
          bestCount = present;
          bestCart = cart;
        }
        if (present === codes.length) {
          syncRememberedFromCart(cart);
          return cart;
        }
      }

      if (bestCart) syncRememberedFromCart(bestCart);
      return bestCart;
    };

    const isShippingLikeCode = (cart, code) => {
      const apps = cart.discount_applications || [];
      if (apps.some((entry) => entry.target_type === 'shipping_line' && codeMatches(entry.title, code))) {
        return true;
      }
      const inCartLevel = (cart.cart_level_discount_applications || []).some((entry) => (
        codeMatches(entry.title, code)
      ));
      const inLineLevel = (cart.items || []).some((item) => (
        (item.line_level_discount_allocations || []).some((allocation) => (
          codeMatches(allocation.discount_application && allocation.discount_application.title, code)
        ))
      ));
      // Present on cart but not reducing product/order total → treat as shipping
      return !inCartLevel && !inLineLevel;
    };

    const markDiscountSuccess = async (cart, code) => {
      syncRememberedFromCart(cart);
      rememberCodes(code);
      input.value = '';
      clearFeedback();
      rememberOpen(true);
      await refreshCartUi();
      const latest = await fetchCart();
      syncRememberedFromCart(latest);
      renderAppliedDiscountBadges(latest);
      if (isShippingLikeCode(latest, code) || findCode(latest, code)) {
        ensureShippingLineVisible();
      }
    };

    const setBusy = (busy) => {
      wrap.classList.toggle('is-loading', busy);
      wrap.setAttribute('aria-busy', busy ? 'true' : 'false');
      btn.classList.toggle('is-loading', busy);
      input.disabled = busy;
      if (label) label.textContent = busy ? messages.applying : messages.apply;
      if (!busy) {
        btn.disabled = !input.value.trim();
      } else {
        btn.disabled = true;
      }
    };

    const showError = (key) => {
      feedback.textContent = messages[key] || messages.generic;
      feedback.className = 'cart-drawer__discount-feedback js-discount-feedback cart-drawer__discount-feedback--error';
    };

    const clearFeedback = () => {
      feedback.textContent = '';
      feedback.className = 'cart-drawer__discount-feedback js-discount-feedback';
    };

    const syncApplyEnabled = () => {
      if (wrap.classList.contains('is-loading')) return;
      btn.disabled = !input.value.trim();
    };

    const rememberOpen = (open) => {
      try {
        sessionStorage.setItem(CART_DRAWER_STORAGE_DISCOUNT, open ? 'true' : 'false');
      } catch (err) {
        /* ignore */
      }
      wrap.classList.toggle('is-open', open);
      const toggle = wrap.querySelector('.js-discount-toggle');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    };

    const applyDiscount = async () => {
      const code = input.value.trim();
      if (!code || wrap.classList.contains('is-loading')) return;

      clearFeedback();
      setBusy(true);

      try {
        const currentCart = await fetchCart();
        const existing = findCode(currentCart, code);

        // Already on cart (including free-shipping codes Shopify may mark pending)
        if (existing) {
          if (existing.applicable === false && !isShippingLikeCode(currentCart, code)) {
            showError('notApplicable');
            rememberOpen(true);
            return;
          }
          await markDiscountSuccess(currentCart, code);
          return;
        }

        const stacked = getStackedCodes(currentCart, code);
        let updatedCart = await applyStackedDiscount(stacked);

        // Never use /discount/CODE when stacking — it replaces other codes
        if (!updatedCart || updatedCart.status) {
          if (stacked.length > 1) {
            showError('generic');
            rememberOpen(true);
            return;
          }
          await fetch(`/discount/${encodeURIComponent(code)}`, {
            method: 'GET',
            redirect: 'follow',
            credentials: 'same-origin'
          });
          updatedCart = await fetchCart();
          syncRememberedFromCart(updatedCart);
        }

        const applied = findCode(updatedCart, code);
        if (applied) {
          await markDiscountSuccess(updatedCart, code);
          return;
        }

        showError('notFound');
      } catch (err) {
        showError('generic');
        console.error('[CartDrawer] Discount error:', err);
      } finally {
        setBusy(false);
      }
    };

    input.addEventListener('input', () => {
      if (feedback.classList.contains('cart-drawer__discount-feedback--error')) {
        clearFeedback();
      }
      syncApplyEnabled();
    });

    btn.addEventListener('click', applyDiscount);

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyDiscount();
      }
    });

    syncApplyEnabled();

    // One-shot: open accordion + show chips when a code came from /discount/...
    // Never call cart refresh here — that caused an open/close loop.
    this.syncDiscountFromCart = async () => {
      if (!wrap || !feedback || this._discountSynced) return;
      this._discountSynced = true;
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = await res.json();
        if (!cart.items || !cart.items.length) return;

        const codes = cart.discount_codes || [];
        syncRememberedFromCart(cart);
        if (!codes.length) return;

        const applicable = codes.filter((entry) => entry.applicable !== false);
        const pending = codes.filter((entry) => entry.applicable === false);

        rememberOpen(true);
        renderAppliedDiscountBadges(cart);

        if (pending.length && !applicable.length) {
          showError('notApplicable');
        } else {
          clearFeedback();
        }
      } catch (err) {
        /* ignore */
      }
    };
  }

  initDiscountAccordion() {
    const wrap = this.querySelector('.js-discount-wrapper');
    const toggle = wrap ? wrap.querySelector('.js-discount-toggle') : null;
    if (!wrap || !toggle) return;

    let remembered = null;
    try {
      remembered = sessionStorage.getItem(CART_DRAWER_STORAGE_DISCOUNT);
    } catch (err) {
      remembered = null;
    }

    if (remembered === 'true') {
      wrap.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    } else {
      wrap.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    if (toggle.dataset.bound === 'true') return;
    toggle.dataset.bound = 'true';
    toggle.addEventListener('click', () => {
      const open = wrap.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
      try {
        sessionStorage.setItem(CART_DRAWER_STORAGE_DISCOUNT, open ? 'true' : 'false');
      } catch (err) {
        /* ignore */
      }
    });
  }

  initSizeEditor() {
    if (this.dataset.sizeEditorBound === 'true') return;
    this.dataset.sizeEditorBound = 'true';

    this.addEventListener('click', (evt) => {
      const btn = evt.target.closest('.js-edit-size');
      if (!btn || !this.contains(btn)) return;
      evt.preventDefault();
      this.openSizeModal(btn);
    });
  }

  openSizeModal(btn) {
    const modal = document.getElementById('CartDrawerSizeModal');
    if (!modal) return;

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    const optionsEl = modal.querySelector('.js-size-modal-options');
    const productEl = modal.querySelector('.js-size-modal-product');
    const variantsScript = btn.parentElement.querySelector('.js-size-variants');
    if (!optionsEl || !variantsScript) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (err) {
      console.error('[CartDrawer] Invalid size variants JSON', err);
      return;
    }

    const sizeIndex = Number(btn.dataset.sizeOptionIndex || 0);
    const currentId = Number(btn.dataset.variantId);
    const optionKeys = ['option1', 'option2', 'option3'];
    const currentOptions = [
      btn.dataset.option1 || null,
      btn.dataset.option2 || null,
      btn.dataset.option3 || null
    ];

    const sizeMap = new Map();
    variants.forEach((variant) => {
      let matches = true;
      optionKeys.forEach((key, index) => {
        if (index === sizeIndex) return;
        const currentVal = currentOptions[index];
        if (currentVal && variant[key] !== currentVal) matches = false;
      });
      if (!matches) return;
      const label = variant[optionKeys[sizeIndex]];
      if (!label || sizeMap.has(label)) return;
      sizeMap.set(label, variant);
    });

    if (productEl) productEl.textContent = btn.dataset.productTitle || '';
    optionsEl.innerHTML = '';

    sizeMap.forEach((variant, label) => {
      const optionBtn = document.createElement('button');
      optionBtn.type = 'button';
      optionBtn.className = 'cart-drawer__size-option';
      optionBtn.textContent = label;
      if (Number(variant.id) === currentId) optionBtn.classList.add('is-current');
      if (!variant.available && Number(variant.id) !== currentId) optionBtn.disabled = true;
      optionBtn.addEventListener('click', () => {
        if (Number(variant.id) === currentId) {
          modal.close();
          return;
        }
        this.swapLineVariant({
          modal,
          optionsEl,
          oldKey: btn.dataset.lineKey,
          quantity: Number(btn.dataset.quantity || 1),
          newVariantId: variant.id
        });
      });
      optionsEl.appendChild(optionBtn);
    });

    modal.open(btn);
  }

  async swapLineVariant({ modal, optionsEl, oldKey, quantity, newVariantId }) {
    optionsEl.querySelectorAll('button').forEach((el) => el.classList.add('is-loading'));
    try {
      await fetch(`${(window.theme && theme.routes && theme.routes.cart) || '/cart'}/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: oldKey, quantity: 0 })
      });

      const addRes = await fetch((window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(newVariantId), quantity: quantity || 1 }] })
      });

      if (!addRes.ok) {
        throw new Error('Unable to add selected size');
      }

      if (modal && typeof modal.close === 'function') modal.close();

      const cartForm = this.querySelector('cart-form');
      if (cartForm && typeof cartForm.refresh === 'function') {
        await cartForm.refresh();
      }
    } catch (err) {
      console.error('[CartDrawer] Size swap failed:', err);
      optionsEl.querySelectorAll('button').forEach((el) => el.classList.remove('is-loading'));
    }
  }
}

if (!customElements.get('cart-drawer')) {
  window.customElements.define('cart-drawer', CartDrawer);
}
